import {Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {firstValueFrom} from 'rxjs';
import {AuthenticationService} from '../../core/service/auth.service';
import {SubscriptionService} from '../../apps/subscriptions/shared/service/subscription.service';
import {Product, ProductList, ProductType} from '../../apps/subscriptions/shared/model/products/product-list.response';
import {LoggingService} from '../../core/service/logging.service';
import {PendingDiscountDto} from '../../apps/subscriptions/shared/model/discount/PendingDiscountDto';
import {TranslateService} from '@ngx-translate/core';
import {LocalizedCurrencyPipe} from '../pipes/localized-currency.pipe';

export type CurrencyCode = 'CZK' | 'EUR' | 'USD';

export interface ProductPrice {
    /** What the customer pays first; the products API has already applied a pending discount. */
    price: number;
    /** Price before the discount, as sent by the API; equals price when no discount applies. */
    originalPrice?: number;
    currency: CurrencyCode;
    /** The products API has already applied this discount to price. */
    appliedDiscount?: PendingDiscountDto;
}

@Injectable({
    providedIn: 'root'
})
export class ProductCacheService {

    private products: Product[] | null = null;
    private appliedDiscount: PendingDiscountDto | null = null;
    private loadingPromise: Promise<Product[]> | null = null;
    private readonly currencyPipe: LocalizedCurrencyPipe;
    /** Account the cached prices belong to; `/products` bakes the caller's pending discount into them. */
    private accountKey: string | null = null;

    constructor(
        private readonly subscriptionService: SubscriptionService,
        private readonly logger: LoggingService,
        translateService: TranslateService,
        authService: AuthenticationService
    ) {
        this.currencyPipe = new LocalizedCurrencyPipe(translateService);
        // The cache lives as long as the page, not the session: logout does not reload, so another
        // account signing in in the same tab would inherit the previous account's discounted prices.
        // A token refresh of the same user keeps the same key and therefore the cache.
        authService.user$.pipe(takeUntilDestroyed()).subscribe(user => {
            const accountKey = user ? JSON.stringify([user.uid, user.tenantId, user.email]) : null;
            if (accountKey !== this.accountKey) {
                this.accountKey = accountKey;
                this.clearCache();
            }
        });
    }

    /**
     * Loads products and caches them. Only a successful, non-empty result is cached.
     * Call this after user login.
     *
     * A failed fetch must NOT poison the cache: `/products` needs the user's Stripe customer,
     * which is provisioned asynchronously after registration — the preload for a brand-new
     * account can hit the 404 window. Caching that `[]` would leave the upgrade modal without
     * a price for the whole session ("Začít od null").
     */
    async loadProducts(): Promise<Product[]> {
        // Return cached products if available
        if (this.products) {
            return this.products;
        }

        // If already loading, wait for that promise
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        // Start loading
        this.loadingPromise = this.fetchProducts();

        try {
            const products = await this.loadingPromise;
            if (products.length > 0) {
                this.products = products;
            }
            return products;
        } finally {
            this.loadingPromise = null;
        }
    }

    private async fetchProducts(): Promise<Product[]> {
        try {
            const productList: ProductList = await firstValueFrom(
                this.subscriptionService.getProducts(ProductType.SERVICE)
            );
            this.appliedDiscount = productList.discount ?? null;
            return productList.products || [];
        } catch (error) {
            this.appliedDiscount = null;
            this.logger.error('Failed to load products', error);
            return [];
        }
    }

    /**
     * Gets cached products or loads them if not cached.
     */
    async getProducts(): Promise<Product[]> {
        return this.loadProducts();
    }

    /**
     * Gets a specific product by plan type (basic, advanced, top).
     */
    async getProductByPlanType(planType: 'basic' | 'advanced' | 'top'): Promise<Product | null> {
        const products = await this.getProducts();
        return products.find(p => p.name.endsWith(`.${planType}`)) || null;
    }

    /**
     * Gets price for a product in the specified currency.
     */
    getProductPrice(product: Product, currency: CurrencyCode): ProductPrice | null {
        const priceObj = product.price.find(p => p.currency.toUpperCase() === currency);
        if (!priceObj) {
            return null;
        }
        return {
            price: priceObj.price,
            ...(priceObj.originalPrice != null ? {originalPrice: priceObj.originalPrice} : {}),
            currency: currency,
            ...(this.appliedDiscount ? {appliedDiscount: this.appliedDiscount} : {})
        };
    }

    /**
     * Gets price for basic product in specified currency.
     */
    async getBasicProductPrice(currency: CurrencyCode): Promise<ProductPrice | null> {
        const product = await this.getProductByPlanType('basic');
        if (!product) {
            return null;
        }
        return this.getProductPrice(product, currency);
    }

    /**
     * Calculates discounted price.
     */
    calculateDiscountedPrice(originalPrice: number, percentOff?: number, amountOff?: number): number {
        if (percentOff) {
            return originalPrice * (1 - percentOff / 100);
        }
        if (amountOff) {
            return Math.max(0, originalPrice - amountOff);
        }
        return originalPrice;
    }

    /**
     * Formats a price the same way the pricing page does (`LocalizedCurrencyPipe`): by the app
     * language, whole amounts without decimals, others with two. The former Intl formatter chose the
     * locale by currency and dropped trailing zeros, so half prices read "184,5 Kč" in the modal.
     */
    formatPrice(price: number, currency: CurrencyCode): string {
        return this.currencyPipe.transform(price, currency, 'symbol');
    }

    /**
     * Clears the cache. Runs automatically whenever the signed-in account changes; call it
     * explicitly when the prices themselves change (a coupon was just applied).
     */
    clearCache(): void {
        this.products = null;
        this.appliedDiscount = null;
        this.loadingPromise = null;
    }
}
